export const formatPhoneNumber = (apiPhoneNumber: string) => {

  const updatedNumber =
    apiPhoneNumber?.startsWith("0") || apiPhoneNumber?.startsWith("1")
      ? apiPhoneNumber.substring(1)
      : apiPhoneNumber;


  const formattedNumber = updatedNumber?.replace(
    /(\d{3})(\d{3})(\d{4})/,
    "($1) $2-$3"
  );

  return formattedNumber;
};

export const cleanHTMLContent = (html: string | undefined): string => {
  if (!html) {
    return "";
  }


  let cleanedHtml = html
    .replace(/\\r\\n/g, "<br/>")
    .replace(/\\n/g, "<br/>")
    .replace(/\\/g, "")
    .replace(/<br\/><br\/>/g, "</p><p>")
    .replace(/^(?!<p>)/, "<p>")
    .replace(/(?!<\/p>)$/, "</p>");


  cleanedHtml = cleanedHtml

    .replace(/<p>([^<]*)<p>/g, "<p>$1</p><p>")
    .replace(/<\/p><\/p>/g, "</p>")
    .replace(/<p><\/p>/g, "")


    .replace(/<(h[1-6])(?![^>]*id)([^>]*)>([^<]*)<\/\1>/gi, (match, tag, attrs, text) => {
      const cleanText = text.trim();
      const id = cleanText.toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      return `<${tag} id="${id}"${attrs}>${cleanText}</${tag}>`;
    })


    .replace(/<li>([^<]+)(?=<(?!\/li>)|$)/gi, "<li>$1</li>")


    // Wrap "loose" text that sits BETWEEN block-level boundaries in a
    // paragraph. The previous version of this rule used `</[^>]+>` for
    // the lookbehind, which also matched inline closers like </a> and
    // </strong> — so well-formed prose like
    //   <p>Lead-in. <a>source</a> verb continues the sentence.</p>
    // got mangled into
    //   <p>Lead-in. <a>source</a><p>verb continues the sentence.</p></p>
    // and the browser then auto-closed the outer <p>, visually splitting
    // one paragraph into two. Scoping the lookbehind to actual block
    // closers prevents that, while still wrapping orphan text between
    // real paragraph/list/heading boundaries.
    .replace(
      /(?<=<\/(?:p|h[1-6]|ol|ul|li|div|section|article|blockquote|pre|table|tr|td|th)>)\s*([^<\s][^<]*[^<\s])\s*(?=<[^>]+>)/g,
      "<p>$1</p>"
    )


    .replace(/\s+/g, ' ')
    .replace(/>\s+</g, '><');

  return cleanedHtml;
};

export const extractTextFromHTML = (html: string): string => {

  return html.replace(/<[^>]*>/g, '').trim();
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

export const formatReadingTime = (text: string): string => {

  const words = text.split(/\s+/).length;
  const minutes = Math.ceil(words / 200);
  return `${minutes} min read`;
};



export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
};

export const throttleRAF = <T extends (...args: unknown[]) => unknown>(
  func: T,
  interval: number = 16
): ((...args: Parameters<T>) => void) => {
  let lastTime = 0;
  let rafId: number | null = null;

  return (...args: Parameters<T>) => {
    const currentTime = performance.now();

    if (currentTime - lastTime >= interval) {
      lastTime = currentTime;
      func(...args);
    } else if (!rafId) {
      rafId = requestAnimationFrame(() => {
        lastTime = performance.now();
        rafId = null;
        func(...args);
      });
    }
  };
};

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number = 300
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
};

const cachedDimensions = {
  width: typeof window !== "undefined" ? window.innerWidth : 0,
  height: typeof window !== "undefined" ? window.innerHeight : 0,
  lastUpdate: 0,
};

export const getWindowDimensions = (forceRefresh: boolean = false) => {
  const now = performance.now();

  if (
    forceRefresh ||
    now - cachedDimensions.lastUpdate > 100 ||
    !cachedDimensions.width ||
    !cachedDimensions.height
  ) {
    if (typeof window !== "undefined") {
      cachedDimensions.width = window.innerWidth;
      cachedDimensions.height = window.innerHeight;
      cachedDimensions.lastUpdate = now;
    }
  }

  return { width: cachedDimensions.width, height: cachedDimensions.height };
};

export const batchRead = <T>(readOperation: () => T): T => {
  // Force a layout calculation before reading
  // This batches all reads together
  if (typeof document !== "undefined" && document.body) {
    void document.body.offsetHeight;
  }

  // Execute all reads in one batch
  return readOperation();
};

// Add new helper for batching multiple reads
export const batchReadMultiple = <T extends Record<string, unknown>>(
  readOperations: { [K in keyof T]: () => T[K] }
): T => {
  // Force layout calculation once
  if (typeof document !== "undefined" && document.body) {
    void document.body.offsetHeight;
  }

  // Execute all reads
  const result = {} as T;
  for (const key in readOperations) {
    const typedKey = key as keyof T;
    result[typedKey] = readOperations[typedKey]();
  }
  return result;
};

